import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import type { ApiClient } from "../api/client";
import type { Message } from "../api/types";
import { QueryCache, type Snapshot } from "./cache";
import { EventStream, type StreamStatus } from "./events";
import { keysForEvent } from "./invalidation";

type Ctx = { api: ApiClient; cache: QueryCache; stream: StreamStatus; setStream: (s: StreamStatus) => void };
const DataContext = createContext<Ctx | null>(null);

export function DataProvider({ api, cache, children }: { api: ApiClient; cache: QueryCache; children: ReactNode }) {
  const [stream, setStream] = useState<StreamStatus>("stopped");
  const value = useMemo(() => ({ api, cache, stream, setStream }), [api, cache, stream]);
  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

/** Connects the realtime stream while mounted (inside the signed-in shell): events invalidate cache keys. */
export function useRealtime(): void {
  const { api, cache, setStream } = useData();
  useEffect(() => {
    const es = new EventStream({
      open: (since) => api.openEvents(since),
      onEvent: (e) => { for (const k of keysForEvent(e)) cache.invalidate(k); },
      onReconnect: () => cache.invalidateAll(),
      onStatus: setStream,
    });
    es.start();
    const nudge = () => { if (document.visibilityState === "visible") es.nudge(); };
    window.addEventListener("online", nudge);
    document.addEventListener("visibilitychange", nudge);
    return () => {
      es.stop();
      window.removeEventListener("online", nudge);
      document.removeEventListener("visibilitychange", nudge);
    };
  }, [api, cache, setStream]);
}

function useData(): Ctx {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("DataProvider missing");
  return ctx;
}
export const useApi = () => useData().api;
export const useCache = () => useData().cache;
export const useStreamStatus = () => useData().stream;

export type QueryResult<T> = Snapshot<T> & { isLoading: boolean; refetch: () => Promise<T | undefined> };

/** Subscribes to a cache key; fetches when missing or stale (or older than staleMs). `key = null` disables. */
export function useQuery<T>(key: string | null, fetcher: () => Promise<T>, opts: { staleMs?: number } = {}): QueryResult<T> {
  const cache = useCache();
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const staleMs = opts.staleMs ?? 30_000;

  const subscribe = useCallback((fn: () => void) => (key ? cache.subscribe(key, fn) : () => undefined), [cache, key]);
  const snap = useSyncExternalStore(subscribe, () => (key ? cache.snapshot<T>(key) : cache.snapshot<T>("\u0000none")));

  useEffect(() => {
    if (!key) return;
    const detach = cache.attach(key, () => fetcherRef.current());
    const s = cache.snapshot<T>(key);
    if (s.data === undefined || s.stale || Date.now() - s.updatedAt > staleMs) void cache.fetch(key).catch(() => undefined);
    return detach;
  }, [cache, key, staleMs]);

  const refetch = useCallback(async () => (key ? cache.fetch<T>(key).catch(() => undefined) : undefined), [cache, key]);
  return { ...snap, isLoading: snap.status === "loading" || (snap.status === "idle" && key !== null), refetch };
}

/** Wraps an action with pending/error state and cache invalidation on success. */
export function useMutation<A extends unknown[], R>(fn: (...args: A) => Promise<R>, opts: { invalidate?: string[]; onSuccess?: (r: R) => void; onError?: (e: unknown) => void } = {}) {
  const cache = useCache();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const mutateAsync = useCallback(async (...args: A): Promise<R> => {
    setPending(true);
    setError(undefined);
    try {
      const r = await fn(...args);
      for (const k of optsRef.current.invalidate ?? []) cache.invalidate(k);
      optsRef.current.onSuccess?.(r);
      return r;
    } catch (e) {
      if (mounted.current) setError(e);
      optsRef.current.onError?.(e);
      throw e;
    } finally {
      if (mounted.current) setPending(false);
    }
  }, [cache, fn]);
  /** Fire-and-forget variant: resolves to undefined on error (onError / `error` carry it). */
  const mutate = useCallback((...args: A): Promise<R | undefined> => mutateAsync(...args).catch(() => undefined), [mutateAsync]);
  return { mutate, mutateAsync, pending, error, reset: () => setError(undefined) };
}

export function mergeMessages(prev: Message[] | undefined, next: Message[]): Message[] {
  const byId = new Map<number, Message>();
  for (const m of prev ?? []) byId.set(m.id, m);
  for (const m of next) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => a.id - b.id);
}

/** The Studio conversation for a series: loads once, then fetches only messages after the last id on each invalidation. */
export function useConversation(seriesId: string) {
  const api = useApi();
  const cache = useCache();
  const key = `messages/${seriesId}`;
  return useQuery<Message[]>(key, async () => {
    const cur = cache.snapshot<Message[]>(key).data;
    const after = cur?.at(-1)?.id;
    const next = await api.listMessages(seriesId, after);
    return after === undefined ? mergeMessages(undefined, next) : mergeMessages(cache.snapshot<Message[]>(key).data, next);
  }, { staleMs: 0 });
}
